import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {first, take} from 'rxjs/operators';
import {IddTraining} from '../interface/idd-training';
import {Uuid} from '../interface/response/uuid';
import {IddTrainingCertificate} from '../interface/idd-training-certificate';

@Injectable({
  providedIn: 'root'
})
export class IddTrainingService {
  public readonly iddTrainingListSubject: ReplaySubject<CountedAndPaginated<IddTraining>>
    = new ReplaySubject<CountedAndPaginated<IddTraining>>(1);
  public readonly iddTrainingNotCountedListSubject: ReplaySubject<ListResponse<IddTraining>>
    = new ReplaySubject<ListResponse<IddTraining>>(1);
  public readonly iddTrainingCertificateListSubject: ReplaySubject<CountedAndPaginated<IddTrainingCertificate>>
    = new ReplaySubject<CountedAndPaginated<IddTrainingCertificate>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  get(iddTrainingId: string): Observable<IddTraining> {
    return this.httpClient.get(`/rest/crm/idd-training/${iddTrainingId}`)
      .pipe(first()) as Observable<IddTraining>;
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/idd-training?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<IddTraining>) => {
        this.iddTrainingListSubject.next(result);
      });
  }

  public loadCertificateList(
    page: number,
    pageSize: number,
    filters: object,
    sort?: {
      sortBy?: string,
      sortOrder?: string
    }
  ): void {
    this.httpClient.get(`/rest/crm/idd-training-certificate?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<IddTrainingCertificate>) => {
        this.iddTrainingCertificateListSubject.next(result);
      });
  }
  public loadCertificateToCheckList(
    page: number,
    pageSize: number,
    filters: object,
    sort?: {
      sortBy?: string,
      sortOrder?: string
    }
  ): void {
    this.httpClient.get(`/rest/crm/idd-training-certificate-to-check?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<IddTrainingCertificate>) => {
        this.iddTrainingCertificateListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<IddTraining>>): void {
    this.httpClient
      .get(`/rest/crm/idd-training?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<IddTraining>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.iddTrainingNotCountedListSubject.next(result);
        }
      });
  }

  downloadReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  downloadReportRegional(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/regional/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  downloadCertificateReport(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training-certificate/method/report?`
      + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  cancelIddTraining(iddTrainingId: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/idd-training/${iddTrainingId}/cancel`, {});
  }

  addCertificate(iddTrainingId: string, formData: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/${iddTrainingId}/certificate`, formData)
      .pipe(first()) as Observable<Uuid>;
  }

 denyCertificate(iddTrainingCertificateId: string, formData: any): Observable<Uuid> {
    return this.httpClient.patch(`/rest/crm/idd-training/certificate/${iddTrainingCertificateId}/reject`, formData)
      .pipe(first()) as Observable<Uuid>;
  }
  approveCertificate(iddTrainingCertificateId: string, formData: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/certificate/${iddTrainingCertificateId}/approve`, formData)
      .pipe(first()) as Observable<Uuid>;
  }

  deleteCertificate(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/idd-training-certificate/${uuid}`)
      .pipe(first());
  }

  downloadCertificate(uuid: string): void {
    this.document.location.href = `/rest/crm/idd-training-certificate/${uuid}/downloadFile`;
  }

  downloadCertificateToCheck(uuid: string): void {
    this.document.location.href = `/rest/crm/idd-training-certificate-to-check/${uuid}/downloadFile`;
  }

  downloadCertificates(uuid: string): void {
    this.document.location.href = `/rest/crm/idd-training/${uuid}/downloadFile`;
  }

  decodeCertificates(formData: any): Observable<any> {
    return this.httpClient.post(`/rest/crm/idd-training-certificate/method/decode`, formData)
      .pipe(first());
  }

  decodeCertificatesInsuranceCompany(formData: any): Observable<any> {
    return this.httpClient.post(`/rest/crm/idd-training-certificate/method/decode/insurance-company`, formData)
      .pipe(first());
  }

  generateCertificates(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/method/generateArchive?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  generateCertificateListArchive(filters: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training-certificate/method/generateArchive?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  addStatement(iddTrainingId: string, formData: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/idd-training/${iddTrainingId}/statement`, formData)
      .pipe(first()) as Observable<Uuid>;
  }

  refreshIddTraining(iddTrainingId: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/idd-training/${iddTrainingId}/refresh`, {});
  }

  getCertificates(iddTrainingCertificateId: string,): Observable<IddTrainingCertificate> {
    return this.httpClient.get(`/rest/crm/idd-training-certificate/${iddTrainingCertificateId}`)
      .pipe(first()) as Observable<IddTrainingCertificate>;
  }

  editCertificate(iddTrainingCertificateId: string, formData: any): Observable<Uuid> {
    return this.httpClient.put(`/rest/crm/idd-training-certificate/${iddTrainingCertificateId}`, formData)
      .pipe(first()) as Observable<Uuid>;
  }
}
